/**
 * Project Work Order Service
 *
 * CRUD client for the Project Work Order entity — a parent grouping that
 * owns a pool of hours and a set of child work orders. Each child WO
 * records its slice of the parent's pool via `allocatedHours`; the sum
 * of allocations is constrained to <= total hours by the backend.
 *
 * Backend endpoints:
 *   GET    /api/project-work-orders                     — List/filter projects
 *   GET    /api/project-work-orders/:id                 — Get one with children
 *   POST   /api/project-work-orders                     — Create (optional inline children)
 *   PATCH  /api/project-work-orders/:id                 — Update project fields
 *   DELETE /api/project-work-orders/:id                 — Soft-delete (children detach)
 *   PATCH  /api/project-work-orders/:id/allocations     — Bulk-set child allocations
 */

import { api } from './api';

/**
 * Lifecycle states. Kept as a wide string union (not enum) so adding a
 * future state on the server side doesn't break the typecheck.
 */
export type ProjectWorkOrderStatus = 'draft' | 'active' | 'completed' | 'cancelled';

/**
 * A minimal view of a child work order as returned by the project's
 * eager-loaded `workOrders` relation. The full WorkOrder type lives in
 * workOrderService.ts; this shape is the subset the project endpoints
 * actually surface.
 */
export interface ProjectChildWorkOrder {
	id: number;
	projectName: string;
	assigneeId: number | null;
	/** Estimated (budgeted) hours for this work order — its slice of the pool. */
	allocatedHours: number | string | null;
	/** Actual hours logged, maintained from the work order's time log entries.
	 *  Only meaningful once the work order has closed out. */
	totalHours?: number | string | null;
	status: string;
	scheduledDate: string | null;
	/** Date the work order was completed, when it has been. */
	completionDate?: string | null;
	/** Eager-loaded assignee — only firstName/lastName from the backend. */
	assignee?: { id: number; firstName: string; lastName: string } | null;
}

/**
 * Full project model as surfaced by the GET endpoints.
 *
 * The hours* fields are computed server-side and attached to both the list
 * and show responses. See the field comments below for how estimated (open)
 * hours differ from actual hours drawn from the pool.
 */
/** Eager-loaded location summary attached to the project. */
export interface ProjectLocation {
	id: number;
	name: string;
	address: string;
	city: string;
	state: string;
	/** Postal code. Backend column is `zip`; camelizeKeys leaves it as
	 *  `zip`, so we expose it under that key here. */
	zip: string;
}

export interface ProjectWorkOrder {
	id: number;
	companyId: number;
	/** Building (location) the project takes place at. Children inherit
	 *  this so the address chain is consistent across the project. */
	locationId: number | null;
	location?: ProjectLocation | null;
	name: string;
	description: string | null;
	/** CMS-managed business-line slug for the whole project. Child work
	 *  orders inherit it on the create form instead of re-picking one,
	 *  so every WO under a project stays on the same line. Null on
	 *  projects created before the field existed. */
	businessLine: string | null;
	totalHours: number | string;
	status: ProjectWorkOrderStatus;
	startDate: string | null;
	endDate: string | null;
	notes: string | null;
	/** Billing-classification tags shared by every child WO (Billable,
	 *  T&M, PW, etc.). */
	billingInfo: string[] | null;
	billingNotes: string | null;
	/** Recurring schedule label (One-Time / Daily / Weekly / ...). */
	frequency: string | null;
	createdBy: number | null;
	createdAt: string;
	updatedAt: string;
	workOrders?: ProjectChildWorkOrder[];
	/* ── Server-computed hours breakdown ──────────────────────────────────
	 * Attached to both list rows and the show response. The pool is charged
	 * for work actually completed: an open child's estimate is earmarked, not
	 * spent, so hoursRemaining deducts hoursActualUsed only. */
	/** Sum of estimates across every child, open or closed (what's committed). */
	hoursAllocated?: number;
	/** Estimated hours on children that have NOT closed out yet. */
	hoursEstimatedOpen?: number;
	/** Estimated hours on children that HAVE closed out. */
	hoursEstimatedClosed?: number;
	/** Actual logged hours on closed-out children — what draws the pool down. */
	hoursActualUsed?: number;
	/** totalHours − hoursActualUsed. */
	hoursRemaining?: number;
	/** hoursRemaining − hoursEstimatedOpen. Forward-looking balance. */
	hoursProjectedRemaining?: number;
	/** hoursActualUsed − hoursEstimatedClosed. Positive = over estimate. */
	hoursVariance?: number;
}

/**
 * Payload for creating a project. `workOrders` is optional — when
 * present, those rows are created and attached in the same transaction.
 */
export interface CreateProjectWorkOrderData {
	companyId: number;
	/** Building (location). Optional in the API payload, required by
	 *  the create form so children always inherit a real address. */
	locationId?: number;
	name: string;
	description?: string;
	/** Business-line slug shared by every child WO. Optional — projects
	 *  can be created without one, in which case children pick their own. */
	businessLine?: string;
	totalHours: number;
	status?: ProjectWorkOrderStatus;
	startDate?: string;
	endDate?: string;
	notes?: string;
	/** Optional billing classification tags shared by every child WO. */
	billingInfo?: string[];
	billingNotes?: string;
	frequency?: string;
	workOrders?: {
		projectName: string;
		locationId: number;
		assigneeId: number;
		description?: string;
		/** Slug from the CMS-managed business_lines table. Omit to inherit
		 *  the project's own businessLine (the API falls back to it). */
		businessLine?: string;
		allocatedHours?: number;
		scheduledDate?: string;
	}[];
}

/** Partial update payload. */
export type UpdateProjectWorkOrderData = Partial<Omit<CreateProjectWorkOrderData, 'workOrders' | 'companyId'>>;

/** Paginated list response. Mirrors Laravel's pagination shape. */
export interface PaginatedProjects {
	data: ProjectWorkOrder[];
	current_page: number;
	last_page: number;
	per_page: number;
	total: number;
}

/** Body shape for the bulk-allocation endpoint. */
export interface AllocationRow {
	workOrderId: number;
	hours: number;
}

export const projectWorkOrderService = {
	list: (params?: Record<string, string>) =>
		api.get<PaginatedProjects>('/project-work-orders', params),

	getById: (id: number | string) =>
		api.get<ProjectWorkOrder>(`/project-work-orders/${id}`),

	create: (data: CreateProjectWorkOrderData) =>
		api.post<ProjectWorkOrder>('/project-work-orders', data),

	update: (id: number | string, data: UpdateProjectWorkOrderData) =>
		api.patch<ProjectWorkOrder>(`/project-work-orders/${id}`, data),

	delete: (id: number | string) =>
		api.delete<void>(`/project-work-orders/${id}`),

	/**
	 * Bulk-update child allocations. The backend validates the sum
	 * against the project's total_hours before writing any row, so the
	 * UI never has to phase the update — it can submit the full set in
	 * a single PATCH.
	 */
	setAllocations: (id: number | string, allocations: AllocationRow[]) =>
		api.patch<ProjectWorkOrder>(`/project-work-orders/${id}/allocations`, { allocations }),
};
